import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { zodResolver } from '@hookform/resolvers/zod';
import type { UseNavigateResult } from '@tanstack/react-router';

import { useMe } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { useCreateEstate } from '@/features/estates/api/estates';
import { EstateForm } from '@/features/estates/components/estate-form';
import { type EstateSchemaType, getCreateEstateSchema } from '@/features/estates/schemas/estate';
import type { UpdateEstateAreas } from '@/features/estates-property/api/estate-areas';
import { useUpdateEstateAreas } from '@/features/estates-property/api/estate-areas';
import { useUpdateEstateDetails } from '@/features/estates-property/api/estate-details';
import { useProject } from '@/features/projects/api/projects';

import { ModalBase } from './modals-base';

export interface CreateProjectObjectModalProps {
  project: {
    id: string;
    name: string;
  };
  navigate: UseNavigateResult<string>;
  onSuccess?: (estateId: string) => void;
}

export const CreateProjectObjectModal: React.FC<CreateProjectObjectModalProps> = ({
  project,
  navigate,
  onSuccess,
}) => {
  const [t] = useTranslation();
  const schema = getCreateEstateSchema();
  const { data: me } = useMe();
  const { data: tenants } = useMyTenants();
  const { data: projectData } = useProject({ id: project.id });

  const { mutateAsync: updateEstateDetails } = useUpdateEstateDetails({});

  const { mutateAsync: updateEstateAreas } = useUpdateEstateAreas({});

  const { mutateAsync: createEstateAsync } = useCreateEstate({
    mutationConfig: {
      onSuccess: async ([createdEstate]) => {
        if (projectData && createdEstate.projectId) {
          const promises: Promise<unknown>[] = [];
          if (projectData.details) {
            promises.push(
              updateEstateDetails({
                id: createdEstate.id,
                constructionType: '',
                condition: '',
                buildingYear: projectData.details.buildingYear || '',
                heatingType: [],
                heatGeneration: '',
                marketType: '',
                usageType: projectData.details.usageType || '',
                typeOfGlass: '',
                flooring: '',
                propertyType: '',
              })
            );
          }
          if (projectData.areaRooms) {
            const updatedAreas: UpdateEstateAreas = {
              id: createdEstate.id,
              totalNumberOfRooms: 0,
            };
            if (
              projectData?.areaRooms.hasElevator !== null &&
              projectData.areaRooms.hasElevator !== undefined
            ) {
              updatedAreas.hasElevator = projectData.areaRooms.hasElevator;
            }
            if ('hasElevator' in updatedAreas) {
              promises.push(updateEstateAreas(updatedAreas));
            }
          }
          await Promise.all(promises);
        }
        if (onSuccess) {
          onSuccess(createdEstate.id);
        } else {
          navigate({ to: '/estates/$estateId', params: { estateId: createdEstate.id } });
        }
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const methods = useForm<EstateSchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      estateData: {
        name: '',
        propertyType: '',
        marketType: '',
        tenantId: tenants[0]?.id ?? '',
        projectId: project.id,
        creatorId: me?.id,
        propertyLevel: PROPERTY_LEVELS.OBJECT,
      },
      addressData: {
        address: '',
        postalCode: '',
        city: '',
        countryId: '',
      },
    },
  });

  useEffect(() => {
    if (projectData) {
      methods.reset((prev) => ({
        ...prev,
        estateData: {
          ...prev.estateData,
          ...(projectData?.details?.marketType && { marketType: projectData.details.marketType }),
          ...(projectData?.details?.propertyType && {
            propertyType: projectData.details.propertyType,
          }),
          ...(projectData?.details?.usageType && { usageType: projectData.details.usageType }),
          ...(projectData?.details?.buildingYear && {
            buildingYear: projectData.details.buildingYear,
          }),
        },
        addressData: {
          ...prev.addressData,
          ...(projectData?.address?.address && {
            addressAutoComplete: projectData.address.address,
            address: projectData.address.address,
          }),
          ...(projectData?.address?.city && { city: projectData.address.city }),
          ...(projectData?.address?.country?.id && { countryId: projectData.address.country.id }),
          ...(projectData?.address?.country?.code && {
            countryCode: projectData.address.country.code,
          }),
          ...(projectData?.address?.postalCode && { postalCode: projectData.address.postalCode }),
          ...(projectData?.address?.street && { street: projectData.address.street }),
          ...(projectData?.address?.houseNumber && {
            houseNumber: projectData.address.houseNumber,
          }),
        },
      }));
      // To trigger the watch()
      if (projectData?.address?.lat && projectData?.address?.long) {
        methods.setValue('addressData.lat', projectData.address.lat);
        methods.setValue('addressData.long', projectData.address.long);
      }
    }
  }, [methods, projectData]);

  const onSubmit = async (args: EstateSchemaType) => {
    await createEstateAsync({
      ...args,
      estateData: {
        ...args.estateData,
        ...(projectData?.energyDetails?.id && {
          estateEnergyDetailsId: projectData.energyDetails.id,
        }),
      },
    });
  };

  return (
    <ModalBase>
      <FormProvider {...methods}>
        <EstateForm
          label={t('add-new-estate')}
          onSubmit={onSubmit}
          options={{
            projectDisabled: true,
            propertyLevelDisabled: true,
            tenantDisabled: true,
          }}
        />
      </FormProvider>
    </ModalBase>
  );
};
